import { action, computed, observable, set, makeObservable } from 'mobx';
import { IFetch } from '../classes/Fetch';
import { constructNewPolicyJacketUI, IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { constructNewPolicyOrderDetails, IPolicyOrderDetails } from '../classes/PolicyOrderDetails';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { IRecordedDeedRequired, IRecordedDeed, constructNewRecordedDeed } from '../classes/RecordedDeed';
import {
  IRecordedMortgageRequired,
  IRecordedMortgage,
  constructNewRecordedMortgage,
} from '../classes/RecordedMortgage';
import { constructNewUnderwriterDataUI, IUnderwriterDataUI } from '../classes/UnderwriterDataUI';
import getListOfPrimaryItems from '../utils/getListOfValidPrimaryItems';
import getPartyFullNames from '../utils/getPartyFullNames';
import valueWasChanged from '../utils/valueWasChanged';
import { ErrorService } from './ErrorService';
import FetchingService from './FetchingService';
import OrderDetailsService from './OrderDetailsService';
import PolicyJacketService from './PolicyJacketService';
import { getOrderDetailsAsync, uploadRecordedDeedAsync, uploadRecordedMortgageAsync } from './PolicyServiceRequest';
import { RecordedDeedService } from './RecordedDeedService';
import { RecordedMortgageService } from './RecordedMortgageService';
import { RootStore } from './RootStore';
import { getPolicyNumberAsync } from './RestAmericanDigitalService';
import { IPolicyNumberError } from '../classes/IPolicyNumberError';
import { ePartyPurpose } from '../../ui-toolkit/enums/ePartyPurpose';
import { baseUrlService } from '../../ui-toolkit/rest/BaseUrlService';

interface IPolicy {
  underwriter: IUnderwriterDataUI;
  orderDetails: IPolicyOrderDetails;
  pageError: string | IPolicyNumberError;
  underwriterLenderPolicyId: string;
}

export class PolicyScheduleAService {
  private policy: IPolicy = {
    orderDetails: observable.object(constructNewPolicyOrderDetails()),
    pageError: '',
    underwriter: observable.object(constructNewUnderwriterDataUI()),
    underwriterLenderPolicyId: '',
  };

  public setUnderwriterPolicyId(value: string) {
    this.policy.underwriterLenderPolicyId = value;
  }

  public get getUnderwriterPolicyId() {
    return this.policy.underwriterLenderPolicyId;
  }

  private recordedDeed = new RecordedDeedService();

  private userRecordedDeedErrors = new ErrorService();

  private policyJacket: PolicyJacketService = new PolicyJacketService();

  private userRecordedDeed: RecordedDeedService = new RecordedDeedService();

  private recordedMortgage = new RecordedMortgageService();

  private userRecordedMortgage: RecordedMortgageService = new RecordedMortgageService();

  private userRecordedMortgageErrors = new ErrorService();

  private loadPage = new FetchingService();

  private updatePageFetch = new FetchingService();

  private saveData = new FetchingService();

  private policyJacketFetch = new FetchingService();

  private policyNumberFetch = new FetchingService();

  private orderDetailsService: OrderDetailsService;

  constructor(rootStore: RootStore) {
    makeObservable<
      PolicyScheduleAService,
      | 'policy'
      | 'recordedDeed'
      | 'userRecordedDeedErrors'
      | 'policyJacket'
      | 'userRecordedDeed'
      | 'recordedMortgage'
      | 'userRecordedMortgage'
      | 'userRecordedMortgageErrors'
      | 'loadPage'
      | 'updatePageFetch'
      | 'saveData'
      | 'policyJacketFetch'
      | 'policyNumberFetch'
      | 'orderDetailsService'
    >(this, {
      policy: observable,
      setUnderwriterPolicyId: action,
      getUnderwriterPolicyId: computed,
      recordedDeed: observable,
      userRecordedDeedErrors: observable,
      policyJacket: observable,
      userRecordedDeed: observable,
      recordedMortgage: observable,
      userRecordedMortgage: observable,
      userRecordedMortgageErrors: observable,
      loadPage: observable,
      updatePageFetch: observable,
      saveData: observable,
      policyJacketFetch: observable,
      policyNumberFetch: observable,
      orderDetailsService: observable,
      initPolicyScheduleAData: action,
      updatePage: action,
      scheduleAIsValid: computed,
      setPageError: action,
      getPageError: computed,
      setUnderwriter: action,
      getUnderwriter: action,
      isAmericanDigital: computed,
      setPolicyNumber: action,
      savePolicyNumber: action,
      setOrderDetails: action,
      getOrderDetails: computed,
      getEndorsements: computed,
      updateRecordedDeedData: action,
      updateRecordedMortgageData: action,
      setRecordedDeeds: action,
      getRecordedDeeds: computed,
      getRecordedDeedRequired: computed,
      getRecordedMortgageRequired: computed,
      setRecordedMortgage: action,
      getRecordedMortgage: computed,
      setUserRecordedMortgage: action,
      getUserRecordedMortgage: computed,
      onCancelUserRecordedMortgage: action,
      getRecordedMortgageWasChangedWithoutError: computed,
      getErrorRecordedMortgage: computed,
      setUserRecordedDeeds: action,
      getUserRecordedDeeds: computed,
      onCancelUserRecordedDeed: action,
      getRecordedDeedWasChangedWithoutError: computed,
      getErrorRecordedDeed: action,
      onPolicyJacketUpload: action,
      onDeletePolicyJacket: action,
      getPolicyJacket: computed,
      getPolicyJacketIsValid: computed,
      policyJacketReadyToSave: computed,
      getPolicyJacketError: computed,
      policyJacketWasChanged: computed,
      policyJacketExist: computed,
      policyNumberWasChanged: computed,
      getDataIsLoaded: computed,
      getSaveFetch: computed,
      getloadPage: computed,
      getPolicyJacketFetch: computed,
      policyJacketFetchSetDefault: action,
      getPolicyNumberFetch: computed,
      policyNumberFetchSetDefault: action,
      uploadPolicyJacket: action,
      savePolicy: action,
      getUpdatePageFetch: computed,
    });

    this.orderDetailsService = rootStore.orderDetailsService;
  }

  public initPolicyScheduleAData = async (policyCategory: PolicyTypeEnum, orderId: string): Promise<void> => {
    this.setPageError('');
    this.loadPage.fetching();
    try {
      await this.orderDetailsService.initOrderDetails(orderId);
      await this.getPageData(policyCategory, orderId);
      if (!this.getPolicyJacket.policyNumber && this.isAmericanDigital) {
        const policyNumber = await getPolicyNumberAsync(orderId, policyCategory);
        this.policyJacket.setPolicyNumber(policyNumber);
      }
      this.loadPage.fetchedSuccess();
      return Promise.resolve();
    } catch (e: any) {
      this.loadPage.fetchedError(e);
      this.setPageError(e);
      return Promise.reject(e);
    }
  };

  public updatePage = async (policyCategory: PolicyTypeEnum, orderId: string) => {
    this.updatePageFetch.fetching();
    this.setPageError('');
    try {
      await this.getPageData(policyCategory, orderId);
      this.updatePageFetch.fetchedSuccess();
      return Promise.resolve();
    } catch (e: any) {
      this.updatePageFetch.fetchedError(e);
      this.setPageError(e);
      this.loadPage.fetchedError(e);
      return Promise.reject(e);
    }
  };

  public get scheduleAIsValid(): boolean {
    const validPrimaryItems = this.getOrderDetails.premiums;
    return (
      this.getPolicyJacketIsValid &&
      !this.getPolicyJacketFetch.fetching &&
      !this.getloadPage.fetching &&
      !this.getSaveFetch.fetching &&
      (validPrimaryItems === null || validPrimaryItems.length === 1)
    );
  }

  public setPageError(e: any) {
    this.policy.pageError = e instanceof Error ? e.toString() : e;
  }

  public get getPageError(): string | IPolicyNumberError {
    return this.policy.pageError;
  }

  public setUnderwriter(underwriterName: string, underwriterWebsite: string) {
    set(
      this.policy.underwriter,
      constructNewUnderwriterDataUI({
        name: underwriterName,
        websiteURL: underwriterWebsite,
      })
    );
  }

  public getUnderwriter(): IUnderwriterDataUI {
    return this.policy.underwriter;
  }

  public get isAmericanDigital(): boolean {
    return this.policy.underwriter.name === 'AmericanDigital';
  }

  public setPolicyNumber = (newPolicyNumber: string | null, orderId: string, error?: string | null) => {
    this.policyNumberFetch.setFetchDefault();
    this.policyJacket.setPolicyNumber(newPolicyNumber, error);
  };

  public savePolicyNumber = async (orderId: string) => {
    if (this.policyJacket.policyNumberWasChanged) {
      this.setPageError('');
      this.policyNumberFetch.fetching();
      try {
        await this.policyJacket.savePolicyNumber(orderId);
        this.policyNumberFetch.fetchedSuccess();
        window.setTimeout(() => {
          this.policyNumberFetch.setFetchDefault();
        }, 1500);
      } catch (e: any) {
        this.policyNumberFetch.fetchedError(e);
        this.setPageError(e);
      }
    }
  };

  public setOrderDetails(data: IPolicyOrderDetails) {
    set(this.policy.orderDetails, data);
  }

  public get getOrderDetails(): IPolicyOrderDetails {
    return this.policy.orderDetails;
  }

  public get getEndorsements(): string[] {
    return this.policy.orderDetails.endorsements;
  }

  public updateRecordedDeedData = async (orderId: string) => {
    if (this.getRecordedDeeds.existing) {
      this.setUserRecordedDeeds(
        {
          ...this.getUserRecordedDeeds,
          existing: false,
        },
        'existing',
        null
      );
    }
    this.setPageError('');
    this.saveData.fetching();
    try {
      await uploadRecordedDeedAsync(orderId, this.getUserRecordedDeeds);
      this.saveData.fetchedSuccess();
      this.setRecordedDeeds(this.getUserRecordedDeeds);
      setTimeout(() => {
        this.saveData.setFetchDefault();
      }, 1500);
    } catch (error: any) {
      if (this.getRecordedDeeds.existing) {
        this.setUserRecordedDeeds(
          {
            ...this.getUserRecordedDeeds,
            existing: true,
          },
          'existing',
          null
        );
      }
      this.setPageError(error);
      this.saveData.fetchedError(error);
    }
  };

  public updateRecordedMortgageData = async (orderId: string) => {
    this.setPageError('');
    this.saveData.fetching();
    try {
      await uploadRecordedMortgageAsync(orderId, this.getUserRecordedMortgage);
      this.saveData.fetchedSuccess();
      this.setRecordedMortgage(this.getUserRecordedMortgage);
      setTimeout(() => {
        this.saveData.setFetchDefault();
      }, 1500);
    } catch (error: any) {
      this.setPageError(error);
      this.saveData.fetchedError(error);
    }
  };

  public setRecordedDeeds(newRecordedDeed: IRecordedDeed) {
    this.recordedDeed.setRecordedDeeds(newRecordedDeed);
  }

  public get getRecordedDeeds(): IRecordedDeed {
    return this.recordedDeed.getRecordedDeeds;
  }

  public get getRecordedDeedRequired(): IRecordedDeedRequired {
    return this.userRecordedDeed.getRequiredFields;
  }

  public get getRecordedMortgageRequired(): IRecordedMortgageRequired {
    return this.userRecordedMortgage.getRequiredFields;
  }

  public setRecordedMortgage(newRecordedMortgage: IRecordedMortgage) {
    this.recordedMortgage.setRecordedMortgage(newRecordedMortgage);
  }

  public get getRecordedMortgage(): IRecordedMortgage {
    return this.recordedMortgage.getRecordedMortgage;
  }

  public setUserRecordedMortgage(
    newRecordedMortgage: IRecordedMortgage,
    propName: string | null,
    error?: string | null
  ) {
    this.userRecordedMortgage.setRecordedMortgage(newRecordedMortgage);
    if (propName) {
      this.userRecordedMortgageErrors.setError(propName, error);
      if (propName === 'document_type') {
        this.userRecordedMortgageErrors.setError('other_document_type', null);
      }
    } else {
      this.userRecordedMortgageErrors.cleanUpErrors();
    }
  }

  public get getUserRecordedMortgage(): IRecordedMortgage {
    return this.userRecordedMortgage.getRecordedMortgage;
  }

  public onCancelUserRecordedMortgage() {
    this.setUserRecordedMortgage(this.getRecordedMortgage, null, null);
  }

  public get getRecordedMortgageWasChangedWithoutError(): boolean {
    return (
      valueWasChanged(this.recordedMortgage.getRecordedMortgage, this.userRecordedMortgage.getRecordedMortgage) &&
      this.userRecordedMortgageErrors.notContainErrors &&
      this.userRecordedMortgage.recordedMortgageReadyForSave
    );
  }

  public get getErrorRecordedMortgage(): object {
    return this.userRecordedMortgageErrors.getErrors;
  }

  public setUserRecordedDeeds(newRecordedDeed: IRecordedDeed, propName: string | null, error?: string | null) {
    this.userRecordedDeed.setRecordedDeeds(newRecordedDeed);
    if (propName) {
      this.userRecordedDeedErrors.setError(propName, error);
      if (propName === 'document_type') {
        this.userRecordedDeedErrors.setError('other_document_type', null);
      }
    } else {
      this.userRecordedDeedErrors.cleanUpErrors();
    }
  }

  public get getUserRecordedDeeds(): IRecordedDeed {
    return this.userRecordedDeed.getRecordedDeeds;
  }

  public onCancelUserRecordedDeed() {
    this.setUserRecordedDeeds(this.recordedDeed.getRecordedDeeds, null, null);
  }

  public get getRecordedDeedWasChangedWithoutError(): boolean {
    return (
      valueWasChanged(this.recordedDeed.getRecordedDeeds, this.userRecordedDeed.getRecordedDeeds) &&
      this.userRecordedDeedErrors.notContainErrors &&
      this.userRecordedDeed.recordedDeedReadyForSave
    );
  }

  public getErrorRecordedDeed(): object {
    return this.userRecordedDeedErrors.getErrors;
  }

  public onPolicyJacketUpload = async (files: FileList | null, orderId: string) => {
    this.policyJacket.uploadPolicyJacket(files);
    if (this.policyJacket.getPolicyJacketIsValid) {
      await this.uploadPolicyJacket(orderId);
    }
  };

  public onDeletePolicyJacket = async (policyType: PolicyTypeEnum, orderId: string) => {
    try {
      this.setPageError('');
      this.policyJacketFetch.fetching();
      await this.policyJacket.deletePolicyJacket(policyType, orderId);
      this.policyJacketFetch.fetchedSuccess();
      window.setTimeout(() => {
        this.policyJacketFetch.setFetchDefault();
      }, 1500);
    } catch (e: any) {
      this.policyJacketFetch.fetchedError(e);
      this.setPageError(e);
    }
  };

  public get getPolicyJacket(): IPolicyJacketUI {
    return this.policyJacket.getPolicyJacket;
  }

  public get getPolicyJacketIsValid(): boolean {
    return this.policyJacket.getPolicyJacketIsValid;
  }

  public get policyJacketReadyToSave(): boolean {
    return this.policyJacket.policyJacketReadyToSave;
  }

  public get getPolicyJacketError(): object {
    return this.policyJacket.getPolicyErrors;
  }

  public get policyJacketWasChanged(): boolean {
    return this.policyJacket.policyJacketWasChanged;
  }

  public get policyJacketExist(): boolean {
    return this.policyJacket.policyJacketExist;
  }

  public get policyNumberWasChanged(): boolean {
    return this.policyJacket.policyNumberWasChanged;
  }

  public get getDataIsLoaded(): boolean {
    return this.loadPage.getFetch.success!;
  }

  public get getSaveFetch(): IFetch {
    return this.saveData.getFetch;
  }

  public get getloadPage(): IFetch {
    return this.loadPage.getFetch;
  }

  public get getPolicyJacketFetch(): IFetch {
    return this.policyJacketFetch.getFetch;
  }

  public policyJacketFetchSetDefault() {
    this.policyJacketFetch.setFetchDefault();
  }

  public get getPolicyNumberFetch(): IFetch {
    return this.policyNumberFetch.getFetch;
  }

  public policyNumberFetchSetDefault() {
    this.policyNumberFetch.setFetchDefault();
  }

  public uploadPolicyJacket = async (orderId: string) => {
    try {
      this.setPageError('');
      this.policyJacketFetch.fetching();
      await this.policyJacket.savePolicyJacket(orderId);
      this.policyJacketFetch.fetchedSuccess();
      window.setTimeout(() => {
        this.policyJacketFetch.setFetchDefault();
      }, 1500);
    } catch (e: any) {
      this.policyJacketFetch.fetchedError(e);
      this.setPageError(e);
    }
  };

  public savePolicy = async (orderId: string) => {
    let result;
    if (this.policyJacketReadyToSave && this.getPolicyJacket.file) {
      result = this.uploadPolicyJacket(orderId);
    } else if (this.policyNumberWasChanged && this.policyJacketExist) {
      result = this.savePolicyNumber(orderId);
    } else {
      result = Promise.resolve('Nothing changed');
    }
    return result;
  };

  public get getUpdatePageFetch(): IFetch {
    return this.updatePageFetch.getFetch;
  }

  private getPageData = async (policyCategory: PolicyTypeEnum, orderId: string): Promise<void> => {
    try {
      await this.getScheduleAData(policyCategory, orderId);
      this.policyJacket.setPolicyCategory(policyCategory);
    } catch (e: any) {
      return Promise.reject(e);
    }
  };

  private getScheduleAData = async (policyCategory: PolicyTypeEnum, orderId: string): Promise<any> => {
    return getOrderDetailsAsync(policyCategory, orderId)
      .then((response) => {
        this.setUnderwriterPolicyId(response.underwriterLenderPolicyId || '');
        /* tslint:disable-next-line */
        this.setUnderwriter(
          response.orderDetails.underwriterName || 'Underwriter',
          response.orderDetails.underwriterWebsite || ''
        );
        this.setOrderDetails(
          constructNewPolicyOrderDetails({
            ...response.orderDetails,
            premiums: getListOfPrimaryItems(policyCategory, response.orderDetails.premiums),
          })
        );
        let recordedDeed = constructNewRecordedDeed();
        if (response.existingDeed) {
          recordedDeed = constructNewRecordedDeed({
            ...response.existingDeed,
            execution_date: response.existingDeed.deed_date,
            existing: true,
          });
        }
        if (response.recordedDeed) {
          recordedDeed = constructNewRecordedDeed({
            ...response.recordedDeed,
            existing: false,
          });
        }
        if (!response.existingDeed && !response.recordedDeed) {
          recordedDeed = constructNewRecordedDeed({
            grantee: getPartyFullNames(
              this.orderDetailsService.getOrderDetails.parties.filter((t) => t.purpose === ePartyPurpose.borrower)
            ),
            grantor: getPartyFullNames(
              this.orderDetailsService.getOrderDetails.parties.filter((t) => t.purpose === ePartyPurpose.seller)
            ),
          });
        }
        this.setRecordedDeeds(recordedDeed);
        this.setUserRecordedDeeds(recordedDeed, null, null);
        if (response.recordedMortgage) {
          this.setRecordedMortgage(constructNewRecordedMortgage(response.recordedMortgage));
          this.setUserRecordedMortgage(constructNewRecordedMortgage(response.recordedMortgage), null, null);
        } else {
          const orderDetails = response.orderDetails as IPolicyOrderDetails;
          this.setUserRecordedMortgage(
            constructNewRecordedMortgage({
              lender_name: orderDetails.nameOfInsured,
              original_loan_amount: orderDetails.liabilityAmount,
              mortgagor_name: orderDetails.borrowers,
            }),
            null
          );
        }
        if (response.shortFormPolicyLink && !response.policyJacket) {
          this.policyJacket.setPolicyJacket(
            constructNewPolicyJacketUI({
              ...response.policyJacket,
              jacketName: response.shortFormPolicyName,
              latestJacketLink: `${baseUrlService.ApiLocation}${response.shortFormPolicyLink}`,
            })
          );
        } else {
          this.policyJacket.setPolicyJacket(constructNewPolicyJacketUI({
            ...response.policyJacket,
            latestJacketLink: `${baseUrlService.ApiLocation}${response.policyJacket?.latestJacketLink}`,
          }));
        }
        return Promise.resolve();
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  };
}
